import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/chat.css';

// Step status icons with animation for active step
const StepStatusIcon = ({ status, isDark }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'completed':
        return { icon: '✓', color: isDark ? '#22C55E' : '#16A34A', bg: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.1)', animate: false };
      case 'error':
      case 'failed':
        return { icon: '✗', color: isDark ? '#EF4444' : '#DC2626', bg: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(220, 38, 38, 0.1)', animate: false };
      case 'in_progress':
        return { icon: '●', color: isDark ? '#3B82F6' : '#2563EB', bg: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.1)', animate: true };
      case 'pending':
      default:
        return { icon: '○', color: isDark ? '#6B7280' : '#9CA3AF', bg: isDark ? 'rgba(107, 114, 128, 0.2)' : 'rgba(156, 163, 175, 0.1)', animate: false };
    }
  };

  const style = getStatusStyle();
  
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 'bold',
        fontSize: '12px',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {style.animate && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            border: `2px solid ${style.color}`,
            animation: 'pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }}
        />
      )}
      {style.icon}
      <style>{`
        @keyframes pulse-ring {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.15);
          }
        }
      `}</style>
    </span>
  );
};

// Define Naukri job posting steps - matches actual Naukri form steps
export const NAUKRI_STEPS = [
  { id: 'browser_setup', name: 'Setup' },
  { id: 'login', name: 'Login' },
  { id: 'analyzing', name: 'Analysing' },
  { id: 'step1', name: 'Job Details' },
  { id: 'step2', name: 'Candidate Preferences' },
  { id: 'step3', name: 'Screening Questions' },
  { id: 'step4', name: 'Job Description' },
  { id: 'step5', name: 'Communication Preferences' },
  { id: 'complete', name: 'Complete' },
];

// Define LinkedIn job posting steps - matches actual LinkedIn Talent Solutions form steps
export const LINKEDIN_STEPS = [
  { id: 'browser_setup', name: 'Setup' },
  { id: 'login', name: 'Login' },
  { id: 'analyzing', name: 'Analyse' },
  { id: 'step1', name: 'Basic Details' },
  { id: 'step2', name: 'Screening Questions' },
  { id: 'step3', name: 'Promote Job' },
  { id: 'step4', name: 'Confirm Job' },
  { id: 'complete', name: 'Complete' },
];

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatStepTime = (value) => {
  const date = toValidDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const withStepTiming = (stepLogs) => {
  Object.values(stepLogs).forEach((step) => {
    const timestamps = step.logs
      .map((log) => log?.timestamp || log?.created_at || null)
      .map(toValidDate)
      .filter(Boolean);

    step.startedAt = timestamps[0]?.toISOString?.() || null;
    step.completedAt = timestamps[timestamps.length - 1]?.toISOString?.() || null;
  });

  return stepLogs;
};

// State machine for categorizing logs sequentially
// Flow: Setup -> Login (when login starts) -> Analyzing (after login success) -> Step 1-5 (when form starts) -> Complete
export const categorizeNaukriLogsIntoSteps = (logs = []) => {
  const stepLogs = {};
  NAUKRI_STEPS.forEach(step => {
    stepLogs[step.id] = { ...step, logs: [], status: 'pending' };
  });
  
  // Track current state in the flow
  let currentStep = 'browser_setup';
  
  // Transition detection functions
  const shouldMoveToLogin = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('login') || 
           lower.includes('found saved naukri session') ||
           lower.includes('navigating to naukri') ||
           lower.includes('session - loading');
  };
  
  const isLoginSuccess = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('logged-in indicator') ||
           lower.includes('login successful') ||
           lower.includes('already logged in') ||
           lower.includes('logout');
  };
  
  const detectStepNumber = (msg) => {
    const lower = msg.toLowerCase();
    // Look for explicit step mentions like "Step 1", "Step 2", etc.
    const stepMatch = lower.match(/step\s*(\d)/);
    if (stepMatch) {
      const stepNum = parseInt(stepMatch[1]);
      if (stepNum >= 1 && stepNum <= 5) {
        return `step${stepNum}`;
      }
    }
    // Also check for filling messages
    if (lower.includes('filling the job posting form') || lower.includes('step 1: filling')) {
      return 'step1';
    }
    if (lower.includes('step 2: filling') || lower.includes('step 2 form')) {
      return 'step2';
    }
    if (lower.includes('step 3: filling') || lower.includes('step 3 form')) {
      return 'step3';
    }
    if (lower.includes('step 4: filling') || lower.includes('step 4 form')) {
      return 'step4';
    }
    if (lower.includes('step 5: filling') || lower.includes('step 5 form')) {
      return 'step5';
    }
    return null;
  };
  
  const isCompletionMessage = (msg) => {
    const lower = msg.toLowerCase();
    // Be more specific - only match FINAL job posting success, not step completions
    // Backend sends: "Job 'X' posted successfully on Naukri", "Job posting completed successfully"
    return lower.includes('job posted successfully') ||
           lower.includes('posted successfully on naukri') ||
           lower.includes('posted successfully on linkedin') ||
           lower.includes('job posting completed successfully') ||
           lower.includes('naukri job posted successfully') ||
           lower.includes('congratulations') ||
           // Match pattern: "Job 'title' posted successfully"
           (lower.includes('job') && lower.includes('posted successfully'));
  };
  
  const isSetupMessage = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('proxy') ||
           lower.includes('apify') ||
           lower.includes('browser started') ||
           lower.includes('browser closed') ||
           lower.includes('launching');
  };
  
  // Process each log sequentially
  logs.forEach(log => {
    const message = log.message || '';
    
    // Check for completion first - these always go to complete
    if (isCompletionMessage(message)) {
      stepLogs['complete'].logs.push(log);
      stepLogs['complete'].status = 'completed';
      return;
    }
    
    // Setup messages always go to setup
    if (currentStep === 'browser_setup' && isSetupMessage(message)) {
      stepLogs['browser_setup'].logs.push(log);
      return;
    }
    
    // State transitions based on current state and message
    if (currentStep === 'browser_setup') {
      if (shouldMoveToLogin(message)) {
        stepLogs['browser_setup'].status = 'completed';
        currentStep = 'login';
      }
    }
    
    if (currentStep === 'login') {
      // After login success, move to analyzing
      if (isLoginSuccess(message)) {
        stepLogs['login'].logs.push(log);
        stepLogs['login'].status = 'completed';
        currentStep = 'analyzing';
        return;
      }
    }
    
    if (currentStep === 'analyzing') {
      // Detect when we move from analyzing to actual form filling
      const stepNum = detectStepNumber(message);
      if (stepNum) {
        stepLogs['analyzing'].status = 'completed';
        currentStep = stepNum;
      }
    }
    
    // Check for step transitions when we're in step1-5
    if (currentStep.startsWith('step')) {
      const stepNum = detectStepNumber(message);
      if (stepNum && stepNum !== currentStep) {
        // Mark previous step as completed
        stepLogs[currentStep].status = 'completed';
        currentStep = stepNum;
      }
    }
    
    // Add log to current step
    if (stepLogs[currentStep]) {
      stepLogs[currentStep].logs.push(log);
      
      // Update step status based on log status
      if (log.status === 'error' || log.status === 'failed') {
        stepLogs[currentStep].status = 'error';
      } else if (stepLogs[currentStep].status === 'pending') {
        stepLogs[currentStep].status = 'in_progress';
      }
    }
  });
  
  // Ensure only ONE step is in_progress at a time
  // Find the last step with logs that's not completed/error
  let lastActiveStepId = null;
  NAUKRI_STEPS.forEach(step => {
    if (stepLogs[step.id].logs.length > 0) {
      lastActiveStepId = step.id;
    }
  });
  
  // Adjust statuses - only the last step with logs should be in_progress
  let foundInProgress = false;
  NAUKRI_STEPS.forEach(step => {
    const stepData = stepLogs[step.id];
    
    // If this step has logs and is not completed/error
    if (stepData.logs.length > 0 && stepData.status !== 'completed' && stepData.status !== 'error') {
      // Only the last active step can be in_progress
      if (step.id === lastActiveStepId && !foundInProgress) {
        stepData.status = 'in_progress';
        foundInProgress = true;
      } else {
        // Previous steps are marked as completed if they have logs
        stepData.status = 'completed';
      }
    }
    
    // Steps without logs should be pending
    if (stepData.logs.length === 0 && stepData.status !== 'completed') {
      stepData.status = 'pending';
    }
  });
  
  const hasCompletionLogs = stepLogs['complete'].logs.length > 0;
  
  // Completion logs are terminal for the step timeline. Sparse or stale
  // earlier step states should not downgrade an already-posted run back to processing.
  if (hasCompletionLogs) {
    stepLogs['complete'].status = 'completed';
  }
  
  return withStepTiming(stepLogs);
};

// State machine for categorizing LinkedIn logs sequentially
// Flow: Setup -> Login -> Analyse -> Step 1 (Basic Details) -> Step 2 (Screening Questions) -> Step 3 (Promote Job) -> Step 4 (Confirm Job) -> Complete
export const categorizeLinkedInLogsIntoSteps = (logs = []) => {
  const stepLogs = {};
  LINKEDIN_STEPS.forEach(step => {
    stepLogs[step.id] = { ...step, logs: [], status: 'pending' };
  });
  
  // Track current state in the flow
  let currentStep = 'browser_setup';
  
  // Transition detection functions for LinkedIn
  const shouldMoveToLogin = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('login') || 
           lower.includes('navigating to linkedin') ||
           lower.includes('session - loading') ||
           lower.includes('linkedin login');
  };
  
  const isLoginSuccess = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('login success') ||
           lower.includes('already logged in') ||
           lower.includes('logged in successfully') ||
           lower.includes('linkedin login success');
  };
  
  const detectLinkedInStepNumber = (msg) => {
    const lower = msg.toLowerCase();
    
    // Step 1: Basic Details - job title, company, location, workplace type
    if (lower.includes('step 1') || 
        lower.includes('basic details') ||
        lower.includes('job title') ||
        lower.includes('workplace type') ||
        lower.includes('company name') ||
        lower.includes('job location') ||
        lower.includes('filling the job posting form') ||
        lower.includes('1/4') ||
        lower.includes('step: 1')) {
      return 'step1';
    }
    
    // Step 2: Screening Questions
    if (lower.includes('step 2') ||
        lower.includes('screening question') ||
        lower.includes('assessments') ||
        lower.includes('2/4') ||
        lower.includes('step: 2')) {
      return 'step2';
    }
    
    // Step 3: Promote Job / Job posting options
    if (lower.includes('step 3') ||
        lower.includes('promote') ||
        lower.includes('job posting option') ||
        lower.includes('reach the right people') ||
        lower.includes('3/4') ||
        lower.includes('step: 3')) {
      return 'step3';
    }
    
    // Step 4: Confirm Job / Final step
    if (lower.includes('step 4') ||
        lower.includes('confirm job') ||
        lower.includes('final button') ||
        lower.includes('finish') ||
        lower.includes('save job') ||
        lower.includes('post job') ||
        lower.includes('4/4') ||
        lower.includes('step: 4')) {
      return 'step4';
    }
    
    return null;
  };
  
  const isCompletionMessage = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('job posted successfully') ||
           lower.includes('posted successfully on linkedin') ||
           lower.includes('job posting completed successfully') ||
           lower.includes('linkedin job posted successfully') ||
           lower.includes('congratulations') ||
           (lower.includes('job') && lower.includes('posted successfully'));
  };
  
  const isSetupMessage = (msg) => {
    const lower = msg.toLowerCase();
    return lower.includes('proxy') ||
           lower.includes('apify') ||
           lower.includes('browser started') ||
           lower.includes('browser closed') ||
           lower.includes('launching') ||
           lower.includes('starting job posting process');
  };
  
  // Process each log sequentially
  logs.forEach(log => {
    const message = log.message || '';
    
    // Check for completion first - these always go to complete
    if (isCompletionMessage(message)) {
      stepLogs['complete'].logs.push(log);
      stepLogs['complete'].status = 'completed';
      return;
    }
    
    // Setup messages always go to setup
    if (currentStep === 'browser_setup' && isSetupMessage(message)) {
      stepLogs['browser_setup'].logs.push(log);
      return;
    }
    
    // State transitions based on current state and message
    if (currentStep === 'browser_setup') {
      if (shouldMoveToLogin(message)) {
        stepLogs['browser_setup'].status = 'completed';
        currentStep = 'login';
      }
    }
    
    if (currentStep === 'login') {
      // After login success, move to analyzing
      if (isLoginSuccess(message)) {
        stepLogs['login'].logs.push(log);
        stepLogs['login'].status = 'completed';
        currentStep = 'analyzing';
        return;
      }
    }
    
    if (currentStep === 'analyzing') {
      // Detect when we move from analyzing to actual form filling
      const stepNum = detectLinkedInStepNumber(message);
      if (stepNum) {
        stepLogs['analyzing'].status = 'completed';
        currentStep = stepNum;
      }
    }
    
    // Check for step transitions when we're in step1-4
    if (currentStep.startsWith('step')) {
      const stepNum = detectLinkedInStepNumber(message);
      if (stepNum && stepNum !== currentStep) {
        // Mark previous step as completed
        stepLogs[currentStep].status = 'completed';
        currentStep = stepNum;
      }
    }
    
    // Add log to current step
    if (stepLogs[currentStep]) {
      stepLogs[currentStep].logs.push(log);
      
      // Update step status based on log status
      if (log.status === 'error' || log.status === 'failed') {
        stepLogs[currentStep].status = 'error';
      } else if (stepLogs[currentStep].status === 'pending') {
        stepLogs[currentStep].status = 'in_progress';
      }
    }
  });
  
  // Ensure only ONE step is in_progress at a time
  let lastActiveStepId = null;
  LINKEDIN_STEPS.forEach(step => {
    if (stepLogs[step.id].logs.length > 0) {
      lastActiveStepId = step.id;
    }
  });
  
  // Adjust statuses - only the last step with logs should be in_progress
  let foundInProgress = false;
  LINKEDIN_STEPS.forEach(step => {
    const stepData = stepLogs[step.id];
    
    if (stepData.logs.length > 0 && stepData.status !== 'completed' && stepData.status !== 'error') {
      if (step.id === lastActiveStepId && !foundInProgress) {
        stepData.status = 'in_progress';
        foundInProgress = true;
      } else {
        stepData.status = 'completed';
      }
    }
    
    if (stepData.logs.length === 0 && stepData.status !== 'completed') {
      stepData.status = 'pending';
    }
  });
  
  const hasCompletionLogs = stepLogs['complete'].logs.length > 0;
  
  // Completion logs are terminal for the step timeline. Sparse or stale
  // earlier step states should not downgrade an already-posted run back to processing.
  if (hasCompletionLogs) {
    stepLogs['complete'].status = 'completed';
  }
  
  return withStepTiming(stepLogs);
};

// Accordion Item Component
export const getJobPostingStepProgressSummary = ({
  logs = [],
  platform = 'Naukri',
  isProcessing = false,
} = {}) => {
  const isLinkedIn = platform.toLowerCase() === 'linkedin';
  const platformSteps = isLinkedIn ? LINKEDIN_STEPS : NAUKRI_STEPS;
  const stepLogs = isLinkedIn
    ? categorizeLinkedInLogsIntoSteps(logs)
    : categorizeNaukriLogsIntoSteps(logs);

  const stepsWithLogs = platformSteps.filter((step) => (stepLogs[step.id]?.logs || []).length > 0);
  const hasLogs = stepsWithLogs.length > 0;
  const activeStep =
    platformSteps.find((step) => ['in_progress', 'error', 'failed'].includes(stepLogs[step.id]?.status)) ||
    [...platformSteps].reverse().find((step) => (stepLogs[step.id]?.logs || []).length > 0) ||
    platformSteps[0];

  const completedSteps = platformSteps.filter((step) => stepLogs[step.id]?.status === 'completed').length;
  const failedStep = platformSteps.find((step) => ['error', 'failed'].includes(stepLogs[step.id]?.status)) || null;
  const activeStepIndex = activeStep ? platformSteps.findIndex((step) => step.id === activeStep.id) : -1;
  const failedStepIndex = failedStep ? platformSteps.findIndex((step) => step.id === failedStep.id) : -1;
  const totalSteps = platformSteps.length;
  const hasCompletedRun = stepLogs.complete?.status === 'completed';
  const currentStepNumber = hasCompletedRun
    ? totalSteps
    : failedStepIndex >= 0
    ? failedStepIndex + 1
    : activeStepIndex >= 0
    ? activeStepIndex + 1
    : Math.min(totalSteps, completedSteps + (hasLogs ? 1 : 0));

  const tone = hasCompletedRun
    ? 'success'
    : failedStep
    ? 'failed'
    : isProcessing || stepLogs[activeStep?.id]?.status === 'in_progress'
    ? 'processing'
    : hasLogs
    ? 'idle'
    : 'queued';

  return {
    hasLogs,
    totalSteps,
    completedSteps,
    currentStepNumber,
    stepCountLabel: `${currentStepNumber}/${totalSteps} steps`,
    stepName: failedStep?.name || activeStep?.name || null,
    tone,
    heading: `${platform} Job Posting Progress`,
    statusLabel:
      tone === 'success'
        ? 'Completed'
        : tone === 'failed'
        ? 'Failed'
        : tone === 'processing'
        ? 'Processing'
        : 'Queued',
  };
};

const StepAccordion = ({ step, isOpen, onToggle, isDark }) => {
  const hasLogs = step.logs.length > 0;
  const isCompleteStep = step.id === 'complete';
  const timestampLabel =
    step.startedAt && step.completedAt && step.startedAt !== step.completedAt
      ? `${formatStepTime(step.startedAt)} • ${formatStepTime(step.completedAt)}`
      : step.completedAt
      ? formatStepTime(step.completedAt)
      : step.startedAt
      ? formatStepTime(step.startedAt)
      : '';
  
  return (
    <div
      style={{
        marginBottom: '8px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${isDark ? 'rgba(75, 85, 99, 0.5)' : 'rgba(229, 231, 235, 1)'}`,
        backgroundColor: isCompleteStep && step.status === 'completed'
          ? (isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)')
          : (isDark ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)'),
      }}
    >
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          cursor: hasLogs ? 'pointer' : 'default',
          color: isDark ? '#F9FAFB' : '#111827',
          opacity: hasLogs ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StepStatusIcon status={step.status} isDark={isDark} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, fontSize: '14px' }}>{step.name}</span>
              {isCompleteStep && step.status === 'completed' && step.logs.length > 0 && (
                <span
                  style={{
                    fontSize: '12px',
                    color: isDark ? '#22C55E' : '#16A34A',
                    fontWeight: 500,
                  }}
                >
                  🎉 Job Posted Successfully!
                </span>
              )}
              {!isCompleteStep && step.logs.length > 0 && (
                <span
                  style={{
                    fontSize: '11px',
                    color: isDark ? '#9CA3AF' : '#6B7280',
                    backgroundColor: isDark ? 'rgba(55, 65, 81, 0.5)' : 'rgba(243, 244, 246, 1)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                  }}
                >
                  {step.logs.length} log{step.logs.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {timestampLabel && (
              <span
                style={{
                  fontSize: '11px',
                  color: isDark ? '#9CA3AF' : '#6B7280',
                }}
              >
                {timestampLabel}
              </span>
            )}
          </div>
        </div>
        {hasLogs && (
          <span
            style={{
              fontSize: '18px',
              color: isDark ? '#9CA3AF' : '#6B7280',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            ▼
          </span>
        )}
      </button>
      
      {isOpen && hasLogs && (
        <div
          style={{
            padding: '8px 16px 16px 52px',
            borderTop: `1px solid ${isDark ? 'rgba(75, 85, 99, 0.5)' : 'rgba(229, 231, 235, 1)'}`,
          }}
        >
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '11px',
              lineHeight: '1.8',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
            className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800"
          >
            {step.logs.map((log, index) => {
              const statusColor = log.status === 'error' || log.status === 'failed'
                ? (isDark ? '#EF4444' : '#DC2626')
                : log.status === 'success' || log.status === 'completed'
                  ? (isDark ? '#22C55E' : '#16A34A')
                  : (isDark ? '#9CA3AF' : '#6B7280');
              
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    marginBottom: '4px',
                    color: isDark ? '#D1D5DB' : '#374151',
                  }}
                >
                  <span style={{ color: statusColor, fontSize: '8px', marginTop: '4px' }}>●</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ wordBreak: 'break-word' }}>{log.message}</div>
                    {log.timestamp && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: isDark ? '#9CA3AF' : '#6B7280',
                          marginTop: '2px',
                        }}
                      >
                        {formatStepTime(log.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * JobPostingStepsLog - Displays job posting logs organized by steps in accordions
 * Supports both Naukri and LinkedIn platforms with their respective steps
 */
const JobPostingStepsLog = ({ 
  logs = [], 
  platform = 'Naukri',
  isProcessing = false,
  onViewFullLog,
  embedded = false,
}) => {
  const { isDark } = useTheme();
  
  // Determine which steps and categorization to use based on platform
  const isLinkedIn = platform.toLowerCase() === 'linkedin';
  const PLATFORM_STEPS = isLinkedIn ? LINKEDIN_STEPS : NAUKRI_STEPS;
  
  // Categorize logs into steps based on platform
  const stepLogs = useMemo(() => {
    return isLinkedIn ? categorizeLinkedInLogsIntoSteps(logs) : categorizeNaukriLogsIntoSteps(logs);
  }, [logs, isLinkedIn]);

  const visibleSteps = useMemo(() => {
    const stepsWithLogs = PLATFORM_STEPS.filter((step) => (stepLogs[step.id]?.logs || []).length > 0);
    return stepsWithLogs.length > 0 ? stepsWithLogs : PLATFORM_STEPS;
  }, [PLATFORM_STEPS, stepLogs]);

  const defaultOpenStepIds = useMemo(() => {
    const preferredStep =
      visibleSteps.find((step) => ['in_progress', 'error', 'failed'].includes(stepLogs[step.id]?.status)) ||
      [...visibleSteps].reverse().find((step) => (stepLogs[step.id]?.logs || []).length > 0) ||
      visibleSteps[0];

    return preferredStep ? new Set([preferredStep.id]) : new Set();
  }, [stepLogs, visibleSteps]);

  const [openSteps, setOpenSteps] = useState(defaultOpenStepIds);

  useEffect(() => {
    setOpenSteps(defaultOpenStepIds);
  }, [defaultOpenStepIds]);
  
  // Toggle accordion
  const toggleStep = (stepId) => {
    const newOpenSteps = new Set(openSteps);
    if (newOpenSteps.has(stepId)) {
      newOpenSteps.delete(stepId);
    } else {
      newOpenSteps.add(stepId);
    }
    setOpenSteps(newOpenSteps);
  };
  
  // Calculate overall progress
  const completedSteps = PLATFORM_STEPS.filter(s => stepLogs[s.id].status === 'completed').length;
  const totalSteps = PLATFORM_STEPS.length;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);
  
  return (
    <div
      style={{
        backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.8)',
        border: `1px solid ${isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)'}`,
        borderRadius: '12px',
        padding: '16px',
        marginBottom: embedded ? 0 : '16px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ minWidth: 0 }}>
            <h6
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: isDark ? '#F9FAFB' : '#111827',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              {platform} Job Posting Progress
              {isProcessing && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#3B82F6',
                    fontWeight: '500',
                    animation: 'pulse 1.5s infinite',
                  }}
                >
                  ● Processing
                </span>
              )}
            </h6>
            <div
              style={{
                fontSize: '12px',
                color: isDark ? '#9CA3AF' : '#6B7280',
                marginTop: '4px',
              }}
            >
              {completedSteps}/{totalSteps} steps
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onViewFullLog}
              disabled={logs.length === 0}
              style={{
                border: 'none',
                borderRadius: '999px',
                padding: '8px 12px',
                background: logs.length === 0
                  ? (isDark ? 'rgba(51, 65, 85, 0.55)' : 'rgba(226, 232, 240, 0.92)')
                  : 'linear-gradient(135deg, #3b5bdb 0%, #28aca8 100%)',
                color: logs.length === 0 ? (isDark ? '#94A3B8' : '#94A3B8') : '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
                boxShadow: logs.length === 0 ? 'none' : '0 12px 24px rgba(59, 91, 219, 0.18)',
                flexShrink: 0,
              }}
            >
              View full log
            </button>
          )}
        </div>
        
        {/* Progress Bar */}
        <div
          style={{
            height: '4px',
            backgroundColor: isDark ? 'rgba(55, 65, 81, 0.5)' : 'rgba(229, 231, 235, 1)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              backgroundColor: '#22C55E',
              borderRadius: '2px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
      
      {/* Steps Accordions */}
      <div>
        {visibleSteps.map((step) => (
          <StepAccordion
            key={step.id}
            step={stepLogs[step.id]}
            isOpen={openSteps.has(step.id)}
            onToggle={() => toggleStep(step.id)}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
};

export default JobPostingStepsLog;
